<template>
  <div
    class="qty-stepper"
    :class="{
      'is-busy': busy,
      'is-disabled': disabled,
      'qty-stepper--pack': isPack,
    }"
  >
    <div class="qty-stepper__box">
      <button
        type="button"
        :disabled="disabled || busy || !canDec"
        :aria-label="decLabel"
        @click="commit(modelValue - stepSize)"
      >
        <AppIcon icon="mdi-minus" :size="16" />
      </button>
      <input
        class="qty-stepper__input"
        type="number"
        inputmode="numeric"
        :value="shownValue"
        :min="shownMin"
        :max="shownMax"
        :step="shownStep"
        :disabled="disabled || busy"
        :aria-label="carton ? '카톤 수량' : '수량'"
        @change="onCommit"
        @blur="onCommit"
      />
      <button
        type="button"
        :disabled="disabled || busy || !canInc"
        :aria-label="incLabel"
        @click="commit(modelValue + stepSize)"
      >
        <AppIcon icon="mdi-plus" :size="16" />
      </button>
    </div>
    <p v-if="carton" class="qty-stepper__caption">
      <strong>카톤</strong>
    </p>
    <p v-else-if="isPack" class="qty-stepper__caption">
      <strong>{{ stepSize.toLocaleString('ko-KR') }}{{ unitNoun }}씩</strong>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import { isOrderPack, snapOrderQty } from '@/utils/mallSell'

const props = withDefaults(
  defineProps<{
    modelValue: number
    min?: number
    max?: number
    step?: number
    unit?: string
    /** 공동구매. 값은 개수이고 화면에는 카톤 수로 보인다. */
    carton?: boolean
    busy?: boolean
    disabled?: boolean
  }>(),
  {
    min: 1,
    max: 9999,
    step: 1,
    unit: '개',
    carton: false,
    busy: false,
    disabled: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const stepSize = computed(() => Math.max(1, Math.trunc(Number(props.step) || 1)))
const isPack = computed(() => isOrderPack(stepSize.value))
const unitNoun = computed(() => props.unit || '개')
const shownValue = computed(() =>
  props.carton ? Math.max(1, Math.round(props.modelValue / stepSize.value)) : props.modelValue,
)
const shownMin = computed(() =>
  props.carton ? Math.max(1, Math.round((Number(props.min) || 1) / stepSize.value)) : props.min,
)
const shownMax = computed(() =>
  props.carton
    ? Math.max(shownMin.value, Math.floor((Number(props.max) || shownMin.value) / stepSize.value))
    : props.max,
)
const shownStep = computed(() => (props.carton ? 1 : stepSize.value))
const decLabel = computed(() =>
  props.carton ? '카톤 줄이기' : isPack.value ? `${stepSize.value}${unitNoun.value} 줄이기` : '수량 줄이기',
)
const incLabel = computed(() =>
  props.carton ? '카톤 늘리기' : isPack.value ? `${stepSize.value}${unitNoun.value} 늘리기` : '수량 늘리기',
)

const canDec = computed(() => props.modelValue > Math.max(1, Number(props.min) || 1))
const canInc = computed(() => {
  const next = snap(props.modelValue + stepSize.value)
  return next > props.modelValue
})

function snap(value: number) {
  return snapOrderQty(value, props.min, stepSize.value, props.max)
}

function commit(value: number) {
  if (props.disabled || props.busy) return
  const next = snap(value)
  if (next === props.modelValue) return
  emit('update:modelValue', next)
}

function onCommit(event: Event) {
  if (props.disabled || props.busy) return
  const input = event.target as HTMLInputElement
  const parsed = Number(String(input.value).trim())
  const fallback = Number(props.min) || 1
  const raw = Number.isFinite(parsed) ? (props.carton ? parsed * stepSize.value : parsed) : fallback
  const next = snap(raw)
  if (next === props.modelValue) {
    input.value = String(shownValue.value)
    return
  }
  emit('update:modelValue', next)
}
</script>

<style lang="scss" src="./QtyStepper.scss"></style>
